// @refresh reset
/* shared/ui — design-system primitives.
   One implementation per component. Pages import; they do not redefine.
   All interaction states (hover/focus/active/disabled) live in CSS classes. */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NexusIcon } from './NexusIcon';

export { NexusIcon };

/* ─── Avatar ────────────────────────────────────────────────── */
export const Avatar = ({ name, tone = 'var(--nx-accent, #125AB0)', size = 24 }) => {
  const initials = (name || '').split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('') || '?';
  return (
    <span className="nx-avatar" style={{ width: size, height: size, background: tone, fontSize: size * 0.42 }}>
      {initials}
    </span>
  );
};

/* ─── Pill ───────────────────────────────────────────────────── */
export const Pill = ({ children, tone = 'gray', size = 'sm' }) => {
  const tones = {
    gray:   { bg: '#F1F4F8', fg: '#475569', dot: '#94a3b8' },
    blue:   { bg: 'var(--nx-accent-tint, #EDF4FD)', fg: 'var(--nx-accent, #125AB0)', dot: 'var(--nx-accent, #125AB0)' },
    green:  { bg: 'var(--nx-success-tint, #E7F5EE)', fg: 'var(--nx-success, #0E8C5A)', dot: 'var(--nx-success, #0E8C5A)' },
    amber:  { bg: '#FEF4E2', fg: '#A56212', dot: '#f59e0b' },
    purple: { bg: 'var(--nx-purple-tint, #ede9fe)', fg: 'var(--nx-purple, #6E3FBA)', dot: 'var(--nx-purple, #6E3FBA)' },
    red:    { bg: '#FDECE8', fg: '#B12A12', dot: '#ef4444' },
    slate:  { bg: '#EEF2F7', fg: '#475569', dot: '#64748b' },
  };
  const t = tones[tone] || tones.gray;
  return (
    <span className="nx-pill" style={{ background: t.bg, color: t.fg, fontSize: size === 'xs' ? 10 : 11 }}>
      <span className="nx-pill-dot" style={{ background: t.dot }} />
      {children}
    </span>
  );
};

/* ─── Btn ────────────────────────────────────────────────────── */
export const Btn = React.forwardRef(({ children, variant = 'ghost', size = 'sm', icon, iconRight, onClick, active, className = '', title, type = 'button', disabled, ...rest }, ref) => {
  const cls = ['nx-btn', `nx-btn-${variant}`, `nx-btn-${size}`, active ? 'is-active' : '', className].filter(Boolean).join(' ');
  return (
    <button ref={ref} type={type} className={cls} onClick={onClick} title={title} disabled={disabled} {...rest}>
      {icon && <NexusIcon name={icon} size={14} />}
      {children && <span>{children}</span>}
      {iconRight && <NexusIcon name={iconRight} size={14} />}
    </button>
  );
});

/* ─── IconBtn ────────────────────────────────────────────────── */
export const IconBtn = ({ icon, onClick, title, 'aria-label': ariaLabel, size = 16, active, className = '', ...rest }) => (
  <button type="button" className={'nx-iconbtn ' + (active ? 'is-active ' : '') + className} onClick={onClick} title={title} aria-label={ariaLabel || title} {...rest}>
    <NexusIcon name={icon} size={size} />
  </button>
);

/* ─── Kbd ────────────────────────────────────────────────────── */
export const Kbd = ({ children }) => <span className="nx-kbd">{children}</span>;

/* ─── Company Score bands (Product-defined, 2026-08-14) ──────── */
export const COMPANY_SCORE_BANDS = [
  { min: 81, max: 100, label: 'Highly Qualified / Strategic Account', color: 'var(--nx-success, #0E8C5A)'       },
  { min: 51, max: 80,  label: 'Good Prospect',                        color: 'var(--nx-accent, #125AB0)'        },
  { min: 21, max: 50,  label: 'Average',                              color: 'var(--nx-warning, #A56212)'       },
  { min: 0,  max: 20,  label: 'Low Quality / Poor Fit',               color: 'var(--nx-text-faint, #94a3b8)'   },
];

export const getScoreBand = (value) => {
  if (value == null) return null;
  const v = Number(value);
  return COMPANY_SCORE_BANDS.find(b => v >= b.min && v <= b.max) ?? COMPANY_SCORE_BANDS[3];
};

/* ─── Score ──────────────────────────────────────────────────── */
export const Score = ({ value }) => {
  if (value == null) return (
    <span className="nx-score">
      <span className="nx-score-num" style={{ color: 'var(--nx-text-faint, #94a3b8)' }}>—</span>
    </span>
  );
  const band = getScoreBand(value);
  const tone = band?.color ?? 'var(--nx-text-faint, #94a3b8)';
  return (
    <span className="nx-score" title={`Score ${value} · ${band?.label ?? ''}`}>
      <span className="nx-score-bar"><span style={{ width: Math.min(Number(value), 100) + '%', background: tone }} /></span>
      <span className="nx-score-num" style={{ color: tone }}>{value}</span>
    </span>
  );
};

/* ─── MenuItem ───────────────────────────────────────────────── */
/* CSS class nx-menu-item handles all hover/focus/disabled states. */
export const MenuItem = ({ children, icon, onClick, danger = false, disabled = false }) => (
  <button
    type="button"
    role="menuitem"
    disabled={disabled}
    className={'nx-menu-item' + (danger ? ' nx-menu-item--danger' : '')}
    onClick={onClick}
  >
    {icon && <NexusIcon name={icon} size={13} />}
    {children}
  </button>
);

/* ─── MenuDivider ────────────────────────────────────────────── */
export const MenuDivider = () => <hr className="nx-menu-divider" />;

/* ─── useClickOutside ────────────────────────────────────────── */
function useClickOutside(ref, handler, active) {
  useEffect(() => {
    if (!active) return;
    const listener = (e) => { if (ref.current && !ref.current.contains(e.target)) handler(e); };
    document.addEventListener('mousedown', listener);
    return () => document.removeEventListener('mousedown', listener);
  }, [ref, handler, active]);
}

/* ─── Menu ───────────────────────────────────────────────────── */
/* Controlled dropdown container. Pages provide open/setOpen state.
   Wraps trigger + .nx-menu panel; handles click-outside close.
   Smart placement: flips upward when insufficient space below.
   useLayoutEffect fires before paint — no visual flash on flip.   */
export const Menu = ({ trigger, open, onClose, children, align = 'right' }) => {
  const ref = useRef(null);
  const panelRef = useRef(null);
  const [openUp, setOpenUp] = useState(false);

  useClickOutside(ref, onClose, open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  // Smart placement — synchronous before paint, zero visual flash.
  // Uses actual rendered panel height when available; falls back to estimate.
  useLayoutEffect(() => {
    if (!open || !ref.current) { setOpenUp(false); return; }
    const rect = ref.current.getBoundingClientRect();
    const menuH = panelRef.current ? panelRef.current.offsetHeight : 140;
    const margin = 8;
    const availBelow = window.innerHeight - rect.bottom;
    const availAbove = rect.top;

    if (availBelow >= menuH + margin) {
      setOpenUp(false);
    } else if (availAbove >= menuH + margin) {
      setOpenUp(true);
    } else {
      // Neither direction fits cleanly — choose the larger side,
      // then perform a minimal smooth scroll on the nearest scrollable ancestor.
      const shouldFlipUp = availAbove > availBelow;
      setOpenUp(shouldFlipUp);
      let scrollable = ref.current.parentElement;
      while (scrollable && scrollable !== document.body) {
        const { overflow, overflowY } = getComputedStyle(scrollable);
        if (/(auto|scroll)/.test(overflow + overflowY)) break;
        scrollable = scrollable.parentElement;
      }
      if (scrollable && scrollable !== document.body) {
        const needed = menuH + margin - (shouldFlipUp ? availAbove : availBelow);
        scrollable.scrollBy({ top: shouldFlipUp ? -needed : needed, behavior: 'smooth' });
      }
    }
  }, [open]);

  const downStyle = align === 'right'
    ? { right: 0, top: '100%', marginTop: 4 }
    : { left: 0, top: '100%', marginTop: 4 };
  const upStyle = align === 'right'
    ? { right: 0, bottom: '100%', marginBottom: 4 }
    : { left: 0, bottom: '100%', marginBottom: 4 };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      {trigger}
      {open && (
        <div ref={panelRef} className="nx-menu" role="menu" style={openUp ? upStyle : downStyle}>
          {children}
        </div>
      )}
    </div>
  );
};

/* ─── SearchInput ────────────────────────────────────────────── */
/* 32 px high input with search icon inset. Consistent across all pages. */
export const SearchInput = ({ value, onChange, placeholder = 'Search…', style, width, autoFocus, onKeyDown }) => (
  <div className="nx-search" style={{ width, ...style }}>
    <NexusIcon name="Search" size={13} className="nx-search-icon" />
    <input
      className="nx-input"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoFocus={autoFocus}
      onKeyDown={onKeyDown}
    />
  </div>
);

/* ─── SearchField ────────────────────────────────────────────── */
/* DS-04: Canonical CRM-module search input (nx-search-wrap / nx-search-input).
   Pass onClear to show a clear button when value is non-empty.
   Set width or minWidth via the style prop so the input does not collapse. */
export const SearchField = ({ value, onChange, onClear, placeholder = 'Search…', width, style, 'aria-label': ariaLabel }) => (
  <span className="nx-search-wrap" style={{ flex: '0 0 auto', width, ...style }}>
    <NexusIcon name="Search" size={13} style={{ color: 'var(--nx-text-faint)', flexShrink: 0, pointerEvents: 'none' }} />
    <input
      className="nx-search-input"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      aria-label={ariaLabel || placeholder}
    />
    {onClear && value && (
      <button type="button" className="nx-search-clear" onClick={onClear} aria-label="Clear search">
        <NexusIcon name="X" size={11} />
      </button>
    )}
  </span>
);

/* ─── Focus trap ─────────────────────────────────────────────── */
/* Shared by Modal and ConfirmDialog. On mount: saves the previously
   focused element and focuses the first focusable child. On unmount:
   restores focus to the trigger. Tab/Shift+Tab cycle within the dialog. */
const FOCUSABLE_SEL = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function useFocusTrap() {
  const containerRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    const focusable = containerRef.current
      ? Array.from(containerRef.current.querySelectorAll(FOCUSABLE_SEL))
      : [];
    if (focusable.length > 0) focusable[0].focus();
    return () => {
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Tab' || !containerRef.current) return;
    const focusable = Array.from(containerRef.current.querySelectorAll(FOCUSABLE_SEL));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }, []);

  return { containerRef, handleKeyDown };
}

/* ─── Modal ──────────────────────────────────────────────────── */
/* Prop-driven modal overlay. Escape key closes. Footer is optional. */
export const Modal = ({ title, icon, onClose, children, footer, width = 480, subHeader }) => {
  const { containerRef, handleKeyDown } = useFocusTrap();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="nx-modal-veil">
      <button
        type="button"
        aria-label="Close dialog"
        tabIndex={-1}
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, border: 'none', cursor: 'default', background: 'transparent', width: '100%', height: '100%' }}
      />
      <dialog
        ref={containerRef}
        open
        aria-modal="true"
        aria-label={title}
        className="nx-modal"
        style={{ position: 'relative', zIndex: 1, width }}
        onKeyDown={handleKeyDown}
      >
        <div className="nx-modal-head">
          {icon && <NexusIcon name={icon} size={16} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />}
          <h2 className="nx-modal-title">{title}</h2>
          <button type="button" className="nx-iconbtn" onClick={onClose} aria-label="Close dialog">
            <NexusIcon name="X" size={14} />
          </button>
        </div>
        {subHeader && <div className="nx-modal-sub-header">{subHeader}</div>}
        <div className="nx-modal-body">{children}</div>
        {footer && <div className="nx-modal-foot">{footer}</div>}
      </dialog>
    </div>
  );
};

/* ─── ConfirmDialog ──────────────────────────────────────────── */
/* Replaces every inline ConfirmModal across org pages.
   Uses existing nx-confirm CSS (unchanged).                        */
export const ConfirmDialog = ({ title, desc, danger = false, busy = false, confirmLabel = 'Confirm', onConfirm, onCancel }) => {
  const { containerRef, handleKeyDown } = useFocusTrap();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    <div className="nx-modal-veil">
      <button
        type="button"
        aria-label="Close dialog"
        tabIndex={-1}
        onClick={onCancel}
        style={{ position: 'absolute', inset: 0, border: 'none', cursor: 'default', background: 'transparent', width: '100%', height: '100%' }}
      />
      <dialog
        ref={containerRef}
        open
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="nx-confirm-title"
        className="nx-confirm"
        style={{ position: 'relative', zIndex: 1 }}
        onKeyDown={handleKeyDown}
      >
        <div className={'nx-confirm-icon' + (danger ? ' is-danger' : '')}>
          <NexusIcon name={danger ? 'AlertTriangle' : 'Info'} size={18} />
        </div>
        <h3 className="nx-confirm-title" id="nx-confirm-title">{title}</h3>
        {desc && <p className="nx-confirm-desc">{desc}</p>}
        <div className="nx-confirm-actions">
          <Btn variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Btn>
          <Btn variant={danger ? 'danger' : 'primary'} disabled={busy} onClick={onConfirm}>
            {confirmLabel}
          </Btn>
        </div>
      </dialog>
    </div>
  );
};

/* ─── EmptyState ─────────────────────────────────────────────── */
/* Standardized empty state. Replaces ad-hoc inline empty messages. */
export const EmptyState = ({ icon = 'Search', title, desc, action }) => (
  <div className="nx-empty">
    {icon && (
      <div className="nx-empty-icon">
        <NexusIcon name={icon} size={20} />
      </div>
    )}
    {title && <p className="nx-empty-title">{title}</p>}
    {desc && <p className="nx-empty-desc">{desc}</p>}
    {action}
  </div>
);
